// src/admin/AdminNav/pages/Recipes.jsx

import React, {useEffect, useState} from "react";
import {supabase} from "../../../../supabaseClient";
import "./recipes.css";

function Recipes() {
    // ------------------------------
    // 1) States for existing recipes
    // ------------------------------
    const [recipes, setRecipes] = useState([]);
    const [recipeSearchTerm, setRecipeSearchTerm] = useState("");

    // ------------------------------
    // 2) Edit form states
    // ------------------------------
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingRecipeId, setEditingRecipeId] = useState(null);

    // Actual form fields for the recipe
    const [receptNamn, setReceptNamn] = useState("");
    const [beskrivning, setBeskrivning] = useState("");
    const [kategori, setKategori] = useState("");
    const [malTyp, setMalTyp] = useState("");
    const [tillagningsinstruktioner, setTillagningsinstruktioner] = useState("");

    // ------------------------------
    // 3) Food item search & chips
    // ------------------------------
    const [selectedFoodItems, setSelectedFoodItems] = useState([]);
    const [foodSearchTerm, setFoodSearchTerm] = useState("");
    const [foodSearchResults, setFoodSearchResults] = useState([]);

    // ------------------------------
    // 4) Fetch all recipes + links on mount
    // ------------------------------
    useEffect(() => {
        const fetchEverything = async () => {
            try {
                // (a) recipes
                const {data: recipeData, error: recipeError} = await supabase.from("recipes").select("*");
                if (recipeError) throw recipeError;

                // (b) links in recipe_fooditems
                const {data: linkData, error: linkError} = await supabase.from("recipe_fooditems").select("recipe_id, food_id");
                if (linkError) throw linkError;

                // (c) all fooditems
                const {data: allFoods, error: foodsError} = await supabase.from("fooditems").select("*");
                if (foodsError) throw foodsError;

                // Build a map
                const recipeMap = {};
                linkData.forEach((link) => {
                    if (!recipeMap[link.recipe_id]) {
                        recipeMap[link.recipe_id] = [];
                    }
                    recipeMap[link.recipe_id].push(link.food_id);
                });

                // Combine data
                const loadedRecipes = recipeData.map((r) => {
                    const foodIds = recipeMap[r.id] || [];
                    const itemNames = foodIds.map((fid) => {
                        const found = allFoods.find((f) => f.id === fid);
                        return found ? found.name : "Okänd matvara";
                    });
                    return {
                        id: r.id,
                        receptNamn: r.name,
                        beskrivning: r.description,
                        kategori: r.category,
                        malTyp: r.meal_type,
                        tillagningsinstruktioner: r.instructions,
                        foodItems: itemNames,
                    };
                });

                setRecipes(loadedRecipes);
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        fetchEverything();
    }, []);

    // ------------------------------
    // 5) Searching for recipes by name
    // ------------------------------
    const filteredRecipes = recipes.filter((r) => r.receptNamn.toLowerCase().includes(recipeSearchTerm.toLowerCase()));

    // ------------------------------
    // 6) Deleting a recipe
    // ------------------------------
    const handleDelete = async (recipeId) => {
        console.log("Attempting to delete recipe:", recipeId);

        try {
            // 1) Delete from recipe_fooditems (to avoid foreign key issues)
            const {error: linkError} = await supabase.from("recipe_fooditems").delete().eq("recipe_id", recipeId);

            if (linkError) {
                console.error("Error deleting recipe-fooditems links:", linkError);
                alert("Kunde inte radera kopplingar för receptet.");
            }

            // 2) Delete from recipes AND see which rows got deleted
            const {data, error} = await supabase.from("recipes").delete().eq("id", recipeId).select(); // <-- This returns the deleted rows
            console.log("Delete data:", data, "error:", error);
            if (error) {
                console.error("Error deleting recipe:", error);
                alert("Kunde inte radera receptet.");
                return;
            }

            // 3) Remove locally
            setRecipes((prev) => prev.filter((r) => r.id !== recipeId));
            alert("Receptet raderades.");
        } catch (err) {
            console.error("Unexpected delete error:", err);
            alert("Ett oväntat fel uppstod vid radering av receptet.");
        }
    };

    // ------------------------------
    // 7) Edit => fetch item objects, scroll top
    // ------------------------------
    const handleEdit = async (recipeId) => {
        const recipeToEdit = recipes.find((x) => x.id === recipeId);
        if (!recipeToEdit) return;

        setReceptNamn(recipeToEdit.receptNamn);
        setBeskrivning(recipeToEdit.beskrivning);
        setKategori(recipeToEdit.kategori);
        setMalTyp(recipeToEdit.malTyp);
        setTillagningsinstruktioner(recipeToEdit.tillagningsinstruktioner);

        // fetch the item IDs
        const {data: linkRows, error: linkError} = await supabase.from("recipe_fooditems").select("food_id").eq("recipe_id", recipeId);

        if (linkError) {
            console.error("Error fetching links for edit:", linkError);
        } else if (linkRows?.length) {
            const ids = linkRows.map((row) => row.food_id);
            // fetch the actual objects
            const {data: itemObjects, error: itemError} = await supabase.from("fooditems").select("*").in("id", ids);

            if (itemError) {
                console.error("Error fetching item objects:", itemError);
            } else {
                setSelectedFoodItems(itemObjects || []);
            }
        } else {
            setSelectedFoodItems([]);
        }

        setEditingRecipeId(recipeId);
        setEditMode(true);
        setShowForm(true);

        window.scrollTo({top: 0, behavior: "smooth"});
    };

    // ------------------------------
    // 8) Searching for "fooditems" instantly
    // ------------------------------
    useEffect(() => {
        const fetchFoodByName = async () => {
            if (!foodSearchTerm.trim()) {
                setFoodSearchResults([]);
                return;
            }
            try {
                const {data, error} = await supabase.from("fooditems").select("*").ilike("name", `%${foodSearchTerm}%`);
                if (error) {
                    console.error("Error searching fooditems:", error);
                    return;
                }
                setFoodSearchResults(data || []);
            } catch (err) {
                console.error("Unexpected error searching fooditems:", err);
            }
        };
        fetchFoodByName();
    }, [foodSearchTerm]);

    // add a food item object to selectedFoodItems
    const handleAddFoodItem = (item) => {
        const alreadySelected = selectedFoodItems.some((f) => f.id === item.id);
        if (!alreadySelected) {
            setSelectedFoodItems((prev) => [...prev, item]);
        }
    };

    // remove a food item by ID
    const handleRemoveFoodItem = (foodId) => {
        setSelectedFoodItems((prev) => prev.filter((obj) => obj.id !== foodId));
    };

    // ------------------------------
    // 9) Save changes => update DB
    // ------------------------------
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!editMode) return;

        try {
            // update main recipe
            const {data: updatedData, error: updateError} = await supabase
                .from("recipes")
                .update({
                    name: receptNamn,
                    description: beskrivning,
                    category: kategori,
                    meal_type: malTyp,
                    instructions: tillagningsinstruktioner,
                })
                .eq("id", editingRecipeId)
                .select();

            if (updateError) {
                console.error("Error updating recipe:", updateError);
                alert("Fel vid uppdatering av recept.");
                return;
            }

            const newRow = updatedData[0];

            // delete old links
            const {error: delError} = await supabase.from("recipe_fooditems").delete().eq("recipe_id", editingRecipeId);
            if (delError) {
                console.error("Error clearing old items:", delError);
            }

            // insert new links
            if (selectedFoodItems.length > 0) {
                const linkRows = selectedFoodItems.map((obj) => ({
                    recipe_id: editingRecipeId,
                    food_id: obj.id,
                }));
                const {error: linkError} = await supabase.from("recipe_fooditems").insert(linkRows);
                if (linkError) {
                    console.error("Error inserting new links:", linkError);
                    alert("Fel vid uppdatering av matvaror.");
                }
            }

            // update local state
            const itemNames = selectedFoodItems.map((obj) => obj.name);
            const updated = recipes.map((r) => {
                if (r.id === editingRecipeId) {
                    return {
                        id: newRow.id,
                        receptNamn: newRow.name,
                        beskrivning: newRow.description,
                        kategori: newRow.category,
                        malTyp: newRow.meal_type,
                        tillagningsinstruktioner: newRow.instructions,
                        foodItems: itemNames,
                    };
                }
                return r;
            });
            setRecipes(updated);

            alert("Recept uppdaterat!");
        } catch (err) {
            console.error("Unexpected error updating recipe:", err);
            alert("Oväntat fel vid uppdatering av recept.");
        }

        // clear
        setReceptNamn("");
        setBeskrivning("");
        setKategori("");
        setMalTyp("");
        setTillagningsinstruktioner("");
        setSelectedFoodItems([]);
        setFoodSearchTerm("");
        setFoodSearchResults([]);
        setEditMode(false);
        setEditingRecipeId(null);
        setShowForm(false);
    };

    // ------------------------------
    // Render
    // ------------------------------
    return (
        <div className="recipes-container">
            <h1>Befintliga Recept</h1>
            <p>Här kan du söka, redigera, eller radera dina redan skapade recept.</p>

            {/* RECIPE SEARCH */}
            <div className="search-bar">
                <label>Sök recept:</label>
                <input type="text" placeholder="Skriv receptnamn..." value={recipeSearchTerm} onChange={(e) => setRecipeSearchTerm(e.target.value)} />
            </div>

            {/* Edit Form */}
            {showForm && (
                <form onSubmit={handleFormSubmit} className="recipe-edit-form">
                    <h2>Redigera recept</h2>

                    <div className="form-group">
                        <label>Recept namn:</label>
                        <input type="text" value={receptNamn} onChange={(e) => setReceptNamn(e.target.value)} required />
                    </div>

                    <div className="form-group">
                        <label>Beskrivning:</label>
                        <input type="text" value={beskrivning} onChange={(e) => setBeskrivning(e.target.value)} required />
                    </div>

                    <div className="form-group">
                        <label>Kategori:</label>
                        <select value={kategori} onChange={(e) => setKategori(e.target.value)} required>
                            <option value="">Välj kategori</option>
                            <option value="allätare">allätare</option>
                            <option value="veg">veg</option>
                            <option value="vegan">vegan</option>
                            <option value="allergi">allergi</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Mål typ:</label>
                        <select value={malTyp} onChange={(e) => setMalTyp(e.target.value)} required>
                            <option value="">Välj mål typ</option>
                            <option value="frukost">frukost</option>
                            <option value="middag">middag</option>
                            <option value="lunch">lunch</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Tillagningsinstruktioner:</label>
                        <textarea rows="3" value={tillagningsinstruktioner} onChange={(e) => setTillagningsinstruktioner(e.target.value)} required />
                    </div>

                    {/* Food item instant search + chips */}
                    <div className="form-group">
                        <label>Ingredienser (instant search):</label>
                        <input type="text" placeholder="Sök matvara..." value={foodSearchTerm} onChange={(e) => setFoodSearchTerm(e.target.value)} className="fooditem-search" />

                        {/* If we have results, user can click to add */}
                        {foodSearchResults.length > 0 && (
                            <ul className="search-results-list">
                                {foodSearchResults.map((item) => (
                                    <li key={item.id} onClick={() => handleAddFoodItem(item)} className="search-result-item">
                                        {item.name}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Show chips for selected items */}
                        {selectedFoodItems.length > 0 && (
                            <div className="selected-fooditems">
                                <p>Valda ingredienser:</p>
                                <div className="selected-items-container">
                                    {selectedFoodItems.map((food) => (
                                        <div key={food.id} className="selected-item-chip">
                                            <span className="chip-text">{food.name}</span>
                                            <span className="chip-remove" onClick={() => handleRemoveFoodItem(food.id)}>
                                                x
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <button type="submit">Spara ändringar</button>
                </form>
            )}

            {/* The list of recipes, filtered by recipeSearchTerm */}
            <div className="existing-recipes-list">
                {filteredRecipes.map((recipe) => (
                    <div key={recipe.id} className="recipe-card">
                        <h3>{recipe.receptNamn}</h3>
                        <p>
                            <strong>Beskrivning:</strong> {recipe.beskrivning}
                        </p>
                        <p>
                            <strong>Kategori:</strong> {recipe.kategori}
                        </p>
                        <p>
                            <strong>Mål typ:</strong> {recipe.malTyp}
                        </p>
                        <p>
                            <strong>Tillagningsinstruktioner:</strong> {recipe.tillagningsinstruktioner}
                        </p>

                        {recipe.foodItems?.length > 0 ? (
                            <>
                                <p>
                                    <strong>Ingredienser:</strong>
                                </p>
                                <ul>
                                    {recipe.foodItems.map((itemName, idx) => (
                                        <li key={idx}>{itemName}</li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <p>(Inga matvaror kopplade)</p>
                        )}

                        <div className="recipe-actions">
                            <button onClick={() => handleEdit(recipe.id)}>Ändra</button>
                            <button className="trash-button" onClick={() => handleDelete(recipe.id)}>
                                🗑
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Recipes;
